import { Box, Tooltip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useGetPatientFlagList } from "../../../../../queries/flags/patientFlags/use-get-patient-flag-list";
import { usePatientFlagArchive } from "../../../../../queries/flags/patientFlags/use-patient-flag-archive";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getValueFromObj,
  getXtenantId,
} from "../../../../../utils/utils";
import UpsertPatientFlag from "./UpsertPatientFlag";
import ViewSingleFlagList from "./ViewSingleFlagList";

// Common avatar styles
const avatarStyles = {
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "2px solid white",
} as const;

const PatientFlagList = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { selectedFlag, flagSearch } = useOutletContext<{
    selectedFlag: any;
    flagSearch: string;
  }>();
  const {
    data: flagList,
    isLoading: tableDataLoading,
    refetch,
  } = useGetPatientFlagList({
    gridSorting,
    pagination,
    flagUuid: selectedFlag?.value,
    searchString: flagSearch,
    xTENANTID: getXtenantId(),
    // active: true, // TODO: need to add active flag
    archive: false,
  });

  const { mutateAsync: archivePatientFlag } = usePatientFlagArchive();

  const [upsertFlagDisclosure, viewSingleFlagListDisclosure, deleteDis] =
    useReduceDisclosure(3);

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "flags",
      flex: 0.6,
      minWidth: 150,
      headerName: "Flag Name",
      renderCell: ({ row }) => (
        <Tooltip title="Temporary Icon to navigate to new patient chart">
          <Text isTabletext>{getValueFromObj(row?.flags)}</Text>
        </Tooltip>
      ),
    },
    {
      field: "flagColour",
      flex: 0.4,
      minWidth: 120,
      headerName: "Flag",
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "41px",
            height: "20px",
            backgroundColor: row?.flagColour,
            borderRadius: "2px",
            display: "inline-block",
          }}
        />
      ),
    },
    {
      field: "patient",
      flex: 0.5,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Patient Count",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
            onClick={() =>
              viewSingleFlagListDisclosure.open({
                uuid: row.uuid,
                flagId: Object.keys(row?.flags ?? {})?.[0],
              })
            }
          >
            <Box display={"flex"} alignItems={"center"} marginLeft={"-8px"}>
              {Object.entries(row?.patient || {})
                .slice(0, 5)
                .map(([uuid, name], index: number) => (
                  <Box
                    {...avatarStyles}
                    color={"#666"}
                    bgcolor={"#e0e0e0"}
                    marginLeft={index > 0 ? "-8px" : 0}
                    key={uuid}
                    zIndex={5 - index}
                  >
                    {(name as string)?.charAt(0)}
                  </Box>
                ))}
            </Box>
            {Object.keys(row?.patient || {}).length > 5 && (
              <Box
                {...avatarStyles}
                bgcolor={"#f5f5f5"}
                marginLeft={"-8px"}
                fontSize={"12px"}
                fontWeight={"bold"}
                color={"#999"}
                zIndex={0}
              >
                +{Object.keys(row?.patient || {}).length - 5}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "modified",
      flex: 0.5,
      minWidth: 160,
      disableColumnMenu: true,
      headerName: "Updated Date",
      renderCell: ({ value }) => (
        <Text isTabletext>{NonNullableDate(value)}</Text>
      ),
    },
    {
      field: "Action",
      width: 90,
      // todo need to fix type
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              { value: "edit", label: "Edit Patient list" },
              // { value: "delete", label: "Delete" },
            ]}
            onActionClick={(v) => {
              if (v === "edit") {
                viewSingleFlagListDisclosure.open({
                  uuid: row.uuid,
                  flagId: Object.keys(row?.flags ?? {})?.[0],
                });
              } else {
                deleteDis.open(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <>
      {upsertFlagDisclosure.opened && (
        <UpsertPatientFlag disclosureHost={upsertFlagDisclosure} />
      )}
      {viewSingleFlagListDisclosure.opened && (
        <ViewSingleFlagList disclosureHost={viewSingleFlagListDisclosure} />
      )}
      {deleteDis.opened && (
        <DeleteModal
          w="380px"
          open={deleteDis.opened}
          onClose={deleteDis.close}
          id={deleteDis?.data}
          onConfirmation={(id) =>
            archivePatientFlag({
              id: id,
              status: true,
              xTENANTID: getXtenantId(),
            }).then((res) => {
              refetch();
              return res;
            })
          }
          content={"You want to delete Patient Flag ?"}
        />
      )}
      <Box flex={1} minHeight={0}>
        <CustomDataGridV2
          loading={tableDataLoading}
          rows={flagList?.data?.content ?? []}
          getRowId={(row) => row?.uuid}
          columns={COLUMNS}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          paginationModel={pagination}
          keepNonExistentRowsSelected
          rowCount={flagList?.data.page.totalElements}
          rowHeight={56}
        />
      </Box>
    </>
  );
};

export default PatientFlagList;
